import { motion } from "framer-motion";
import { Zap, SlidersHorizontal, ShieldCheck } from "lucide-react";
import { useI18n } from "../i18n";
import Tilt from "./Tilt";

const BG = "https://images.unsplash.com/photo-1658660854207-8886b1d69bb8?crop=entropy&cs=srgb&fm=jpg&q=85";

function Card({ children, className = "", innerClass = "", delay = 0, testId }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay }}
            className={`h-full rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl ${className}`}
            data-testid={testId}
        >
            <Tilt className={`h-full ${innerClass}`}>{children}</Tilt>
        </motion.div>
    );
}

export default function Bento() {
    const { t } = useI18n();
    return (
        <section className="relative py-24 sm:py-32" data-testid="section-bento">
            <div className="mx-auto max-w-6xl px-5 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.15 }}
                    transition={{ duration: 0.7 }}
                    className="mb-14"
                >
                    <p className="mb-3 font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-brand-blue">
                        {t.w1}
                    </p>
                    <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
                        {t.w2a} <span className="text-gradient">{t.w2b}</span>
                    </h2>
                </motion.div>

                <div className="grid gap-5 md:grid-cols-3 md:grid-rows-[1fr_1fr_auto]">
                    <Card testId="bento-rumus" className="relative overflow-hidden md:col-span-2 md:row-span-2">
                        <img
                            src={BG}
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover opacity-25"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
                        <div className="relative flex h-full flex-col justify-between p-8 sm:p-10">
                            <h3 className="max-w-xs font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
                                {t.w3t}
                            </h3>
                            <div>
                                <div className="flex flex-wrap items-center gap-2 font-mono text-xs sm:text-sm">
                                    {[`( ${t.fH}`, "÷", `${t.fD} )`, "×", t.fS, "×", t.f5].map((tk, i) => (
                                        <span
                                            key={i}
                                            className={`rounded-lg border px-3 py-1.5 ${
                                                tk === "×" || tk === "÷"
                                                    ? "border-white/10 text-zinc-500"
                                                    : "border-brand-violet/40 bg-brand-violet/10 text-white"
                                            }`}
                                        >
                                            {tk}
                                        </span>
                                    ))}
                                </div>
                                <p className="mt-4 max-w-md text-sm leading-relaxed text-zinc-400">{t.wfd}</p>
                            </div>
                        </div>
                    </Card>

                    <Card testId="bento-realtime" delay={0.1} innerClass="p-8">
                        <Zap size={22} className="text-brand-coral" />
                        <h3 className="mt-4 font-display text-lg font-bold">{t.w4t}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-zinc-400">{t.w4d}</p>
                    </Card>

                    <Card testId="bento-rate" delay={0.2} innerClass="p-8">
                        <SlidersHorizontal size={22} className="text-brand-violet" />
                        <h3 className="mt-4 font-display text-lg font-bold">{t.w5t}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-zinc-400">{t.w5d}</p>
                    </Card>

                    <Card testId="bento-privat" delay={0.3} className="md:col-span-3" innerClass="flex items-center gap-6 p-8">
                        <ShieldCheck size={26} className="shrink-0 text-brand-blue" />
                        <div>
                            <h3 className="font-display text-lg font-bold">{t.w6t}</h3>
                            <p className="mt-1 text-sm leading-relaxed text-zinc-400">{t.w6d}</p>
                        </div>
                    </Card>
                </div>
            </div>
        </section>
    );
}
